import { FC } from 'react';

export interface GenericTimelineEvent {
  textA: string;
  textB: string;
  textC: string;
  eventTag: string;
  colourCode: string;
  isLatest: boolean;
  webCtaList?: {
    text: string;
    action: string;
    textColour: string;
    backgroundColour: string;
    borderColour: string;
  }[];
}

export interface GenericTimelineProps {
  events: GenericTimelineEvent[];
  onButtonClick?: (action: string) => void;
  className?: string;
  isActionLoading?: boolean;
}

const GenericTimeline: FC<GenericTimelineProps> = ({ events, onButtonClick, className = '', isActionLoading }) => {
  return (
    <div className={`${className}`}>
      {events?.map((event, index) => (
        <div className="flex gap-3" key={`${event.eventTag}-${index}`}>
          <div className="mt-0.5 flex flex-col items-center">
            <div
              className={`relative flex h-4 w-4 shrink-0 ${
                event.isLatest ? 'items-center' : 'items-start'
              } justify-center`}>
              {event.isLatest ? (
                <>
                  <div
                    className="absolute h-[18px] w-[18px] rounded-full border-2"
                    style={{
                      borderColor: event.colourCode
                    }}
                  />
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{
                      backgroundColor: event.colourCode
                    }}
                  />
                </>
              ) : (
                <div className="h-2 w-2 rounded-full bg-gray-1" />
              )}
            </div>
            {index !== events.length - 1 && <div className="-mt-2 h-full w-px bg-secondaryBtn" />}
          </div>

          <div className={`-mt-0.5 flex flex-col ${index !== events.length - 1 ? 'pb-4' : ''}`}>
            {event.textA && <div className="text-sbase font-medium leading-tight text-primaryText">{event.textA}</div>}

            {event.textB && <div className="mt-1 whitespace-pre-line text-px13 text-primaryText">{event.textB}</div>}

            {event.textC && <div className="mt-1 text-px13 text-accentText">{event.textC}</div>}

            {event?.webCtaList?.length && (
              <div className="mt-2 flex flex-wrap gap-2">
                {event.webCtaList.map((cta, ctaIndex) => (
                  <button
                    key={ctaIndex}
                    className="inline-flex items-center rounded-md border px-3 py-1.5 text-sbase font-semibold 
                      hover:opacity-80 focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                    style={{
                      color: cta.textColour,
                      backgroundColor: cta.backgroundColour,
                      borderColor: cta.borderColour
                    }}
                    disabled={isActionLoading}
                    onClick={() => onButtonClick?.(cta.action)}
                    type="button"
                    aria-label={`Action: ${cta.text}`}>
                    {cta.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default GenericTimeline;
